import { LoadingStageModel, ValueModel } from '@kts-front/models';
import { ILocalStore, Nullable } from '@kts-front/types';
import { debounce } from '@kts-front/utils';
import { action, computed, makeObservable } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { NotificationsStore } from 'stores/global/RootStore/NotificationsStore';
import { CommonApiError } from 'types/ApiError';
import { formatPhoneForBackend } from 'utils/formatPhoneForBackend';
import { isCancelledRequest } from 'utils/isCancelledRequest';
import { validatePhoneNumber } from 'utils/validators/validators';

import { CheckUniqueClientMethod } from '../RootStore/AppConfigStore';

type ClientUniquenessStoreOptions = {
  checkUniquePhone?: boolean;
};

export class ClientUniquenessStore implements ILocalStore {
  readonly notificationsStore = new NotificationsStore();
  readonly projectId = new ValueModel<Nullable<number>>(null);
  readonly checkUniqueClient = new ValueModel<CheckUniqueClientMethod>(CheckUniqueClientMethod.phoneOnly);
  readonly isUnique = new ValueModel<Nullable<boolean>>(null);
  readonly loadingStageUnique = new LoadingStageModel();

  private readonly _checkUniquePhone: boolean;
  private readonly _uniqueCheckRequest = apiStore.createRequest({
    url: ENDPOINTS.client.unique,
  });

  constructor(options?: ClientUniquenessStoreOptions) {
    this._checkUniquePhone = options?.checkUniquePhone ?? false;
    makeObservable(this, {
      shouldCheckUniqueByPhoneOnly: computed,
      shouldCheckUniqueByPhoneAndProject: computed,
      isClientNotUnique: computed,
      shouldHideDetails: computed,
      checkUnique: action.bound,
      setUniqueResult: action.bound,
    });
  }

  get shouldCheckUniqueByPhoneOnly(): boolean {
    return this.checkUniqueClient.value === CheckUniqueClientMethod.phoneOnly;
  }

  get shouldCheckUniqueByPhoneAndProject(): boolean {
    return this.checkUniqueClient.value === CheckUniqueClientMethod.phoneAndProject;
  }

  get isClientNotUnique(): boolean {
    return this.shouldCheckUniqueByPhoneAndProject && this.isUnique.value === false;
  }

  get shouldHideDetails(): boolean {
    return (
      this.shouldCheckUniqueByPhoneAndProject && (this.isUnique.value === false || !this.loadingStageUnique.isSuccess)
    );
  }

  checkUnique(phone: string | null): void {
    if (!this._checkUniquePhone) {
      this.isUnique.change(null);

      return;
    }

    if (phone && !validatePhoneNumber()(phone).value) {
      const formattedPhone = formatPhoneForBackend(phone);

      if (formattedPhone) {
        this._checkUniqueDebounced(formattedPhone);
      } else {
        this.isUnique.change(null);
      }
    } else {
      this.isUnique.change(null);
    }
  }

  setUniqueResult(isUnique: boolean | null): void {
    this.isUnique.change(isUnique);
  }

  private _checkUniqueDebounced = debounce({
    func: async (phone: string) => {
      this.loadingStageUnique.loading();

      const params: { phone: string; project_id?: number } = { phone };

      if (this.shouldCheckUniqueByPhoneAndProject && this.projectId.value) {
        params.project_id = this.projectId.value;
      }

      const response = await this._uniqueCheckRequest.call<any, CommonApiError>({
        params,
      });

      if (response.isError) {
        if (isCancelledRequest(response)) {
          return;
        }
        this.notificationsStore.showErrorByResponse(response);
        this.isUnique.change(null);
        this.loadingStageUnique.error();

        logApiErrWithSentry({
          endpoint: ENDPOINTS.client.unique,
          method: 'GET',
          requestPayload: params,
          response,
        });

        return;
      }

      this.isUnique.change(response.data.status);
      this.loadingStageUnique.success();
    },
    timeout: 500,
  });

  destroy(): void {}
}
